import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

const CustomerDetailsPage = () => {
    const { bookingReference } = useParams();
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [booking, setBooking] = useState(null);
    const [customerDetails, setCustomerDetails] = useState(null);
    const [divers, setDivers] = useState([{
        name: '',
        height: '',
        weight: '',
        shirt_size: '',
        shoe_size: ''
    }]);
    const [healthForm, setHealthForm] = useState({
        allergies: '',
        dietary_requirements: ''
    });
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    const backendUrl = process.env.REACT_APP_BACKEND_URL;

    useEffect(() => {
        if (bookingReference) {
            fetchCustomerDetails();
        }
    }, [bookingReference]);

    const fetchCustomerDetails = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${backendUrl}/api/customer-details/${bookingReference}`);
            
            if (!response.ok) {
                throw new Error('Booking not found');
            }

            const data = await response.json();
            setBooking(data.booking);
            setCustomerDetails(data.customer_details);

            // Initialize divers array based on participants count
            const numParticipants = data.booking?.participants || 1;
            
            // Pre-fill form if details exist
            if (data.customer_details?.form_completed) {
                // If we have saved divers data, use it
                if (data.customer_details.divers && data.customer_details.divers.length > 0) {
                    setDivers(data.customer_details.divers);
                } else {
                    // Legacy single-diver data - convert to new format
                    const legacyDiver = {
                        name: data.booking?.customer_name || '',
                        height: data.customer_details.height || '',
                        weight: data.customer_details.weight || '',
                        shirt_size: data.customer_details.shirt_size || '',
                        shoe_size: data.customer_details.shoe_size || ''
                    };
                    
                    // Create array with remaining empty divers if needed
                    const diversArray = [legacyDiver];
                    for (let i = 1; i < numParticipants; i++) {
                        diversArray.push({ name: '', height: '', weight: '', shirt_size: '', shoe_size: '' });
                    }
                    setDivers(diversArray);
                }
                
                setHealthForm({
                    allergies: data.customer_details.allergies || '',
                    dietary_requirements: data.customer_details.dietary_requirements || ''
                });
                setSuccess(true);
            } else {
                // New booking - create empty divers array
                const diversArray = [];
                for (let i = 0; i < numParticipants; i++) {
                    diversArray.push({
                        name: i === 0 ? (data.booking?.customer_name || '') : '',
                        height: '',
                        weight: '',
                        shirt_size: '',
                        shoe_size: ''
                    });
                }
                setDivers(diversArray);
            }

        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDiverChange = (index, field, value) => {
        setDivers(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], [field]: value };
            return updated;
        });
    };

    const handleHealthChange = (e) => {
        const { name, value } = e.target;
        setHealthForm(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setError('');

        try {
            // Combine divers data with health info
            const formData = {
                divers: divers,
                // Also keep legacy fields for backwards compatibility
                height: divers[0]?.height || '',
                weight: divers[0]?.weight || '',
                shirt_size: divers[0]?.shirt_size || '',
                shoe_size: divers[0]?.shoe_size || '',
                allergies: healthForm.allergies,
                dietary_requirements: healthForm.dietary_requirements
            };

            const response = await fetch(`${backendUrl}/api/customer-details/${bookingReference}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData),
            });

            if (!response.ok) {
                throw new Error('Failed to save details');
            }

            setSuccess(true);
            await fetchCustomerDetails(); // Refresh data

        } catch (err) {
            setError(err.message);
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading your adventure profile...</p>
                </div>
            </div>
        );
    }

    if (error && !booking) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="max-w-md mx-auto text-center bg-white p-8 rounded-lg shadow-lg">
                    <div className="text-red-500 text-6xl mb-4">⚠️</div>
                    <h1 className="text-2xl font-bold text-gray-900 mb-4">Booking Not Found</h1>
                    <p className="text-gray-600 mb-6">{error}</p>
                    <a 
                        href="/" 
                        className="inline-block bg-orange-500 text-white px-6 py-3 rounded-lg hover:bg-orange-600 transition-colors"
                    >
                        Return to Home
                    </a>
                </div>
            </div>
        );
    }

    const numDivers = booking?.participants || 1;

    return (
        <div className="min-h-screen bg-gray-50 py-8 px-4">
            <div className="max-w-3xl mx-auto">
                {/* Header */}
                <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white p-8 rounded-t-lg">
                    <div className="text-center">
                        <h1 className="text-3xl font-bold mb-2">🎯 Complete Your Adventure Profile</h1>
                        <p className="text-orange-100">Help us prepare the perfect gear for your adventure!</p>
                    </div>
                </div>

                {/* Booking Info */}
                {booking && (
                    <div className="bg-white p-6 border-l-4 border-orange-500">
                        <h2 className="text-lg font-semibold text-gray-900 mb-4">Your Adventure Details</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                            <div>
                                <span className="font-medium text-gray-600">Adventure:</span>
                                <p className="text-gray-900">{booking.adventure_title}</p>
                            </div>
                            <div>
                                <span className="font-medium text-gray-600">Date:</span>
                                <p className="text-gray-900">{booking.booking_date}</p>
                            </div>
                            <div>
                                <span className="font-medium text-gray-600">Total Divers:</span>
                                <p className="text-gray-900 font-semibold text-lg">{numDivers} {numDivers === 1 ? 'person' : 'people'}</p>
                            </div>
                            <div>
                                <span className="font-medium text-gray-600">Receipt #:</span>
                                <p className="text-gray-900 font-mono">{booking.booking_reference}</p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Success Message */}
                {success && (
                    <div className="bg-green-50 border-l-4 border-green-500 p-6">
                        <div className="flex items-center">
                            <div className="text-green-500 text-2xl mr-3">✅</div>
                            <div>
                                <h3 className="text-green-800 font-semibold">Profile Complete!</h3>
                                <p className="text-green-700">Thank you! We have all the information we need to prepare for your adventure.</p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Form */}
                <div className="bg-white p-8 rounded-b-lg shadow-lg">
                    <form onSubmit={handleSubmit} className="space-y-8">
                        <div className="text-center mb-8">
                            <h2 className="text-2xl font-bold text-gray-900 mb-2">
                                {success ? 'Update Your Information' : 'Tell Us About Your Group'}
                            </h2>
                            <p className="text-gray-600">
                                {numDivers > 1 
                                    ? `Please provide sizing details for all ${numDivers} divers in your group.`
                                    : 'This information helps us provide the right equipment and ensure your safety.'}
                                <br />
                                <span className="text-sm text-gray-500">⏰ Please complete 48 hours before your adventure date</span>
                            </p>
                        </div>

                        {error && (
                            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                                {error}
                            </div>
                        )}

                        {/* Diver Information Sections */}
                        {divers.map((diver, index) => (
                            <div key={index} className="bg-blue-50 p-6 rounded-lg border border-blue-200">
                                <h3 className="text-lg font-semibold text-blue-900 mb-4">
                                    🤿 {numDivers > 1 ? `Diver ${index + 1}` : 'Diver'} - Sizing Information
                                </h3>
                                
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {/* Diver Name */}
                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Diver Name <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={diver.name}
                                            onChange={(e) => handleDiverChange(index, 'name', e.target.value)}
                                            placeholder="Full name"
                                            required
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
                                        />
                                    </div>

                                    {/* Height Field */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Height <span className="text-gray-500">(Optional)</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={diver.height}
                                            onChange={(e) => handleDiverChange(index, 'height', e.target.value)}
                                            placeholder="e.g., 175 cm or 5'9&quot;"
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
                                        />
                                        <p className="mt-1 text-sm text-gray-500">
                                            For wetsuit fitting
                                        </p>
                                    </div>

                                    {/* Weight Field */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Weight <span className="text-gray-500">(Optional)</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={diver.weight}
                                            onChange={(e) => handleDiverChange(index, 'weight', e.target.value)}
                                            placeholder="e.g., 70 kg or 154 lbs"
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
                                        />
                                        <p className="mt-1 text-sm text-gray-500">
                                            For wetsuit and weight belt fitting
                                        </p>
                                    </div>

                                    {/* Shirt Size Field */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Shirt Size <span className="text-gray-500">(Optional)</span>
                                        </label>
                                        <select
                                            value={diver.shirt_size}
                                            onChange={(e) => handleDiverChange(index, 'shirt_size', e.target.value)}
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
                                        >
                                            <option value="">Select size...</option>
                                            <option value="XS">XS</option>
                                            <option value="S">S</option>
                                            <option value="M">M</option>
                                            <option value="L">L</option>
                                            <option value="XL">XL</option>
                                            <option value="XXL">XXL</option>
                                            <option value="3XL">3XL</option>
                                        </select>
                                    </div>

                                    {/* Shoe Size Field */}
                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Shoe/Fin Size <span className="text-gray-500">(Optional)</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={diver.shoe_size}
                                            onChange={(e) => handleDiverChange(index, 'shoe_size', e.target.value)}
                                            placeholder="e.g., US 10, EU 43, or AU 9"
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
                                        />
                                        <p className="mt-1 text-sm text-gray-500">
                                            For dive boots and fins
                                        </p>
                                    </div>
                                </div>
                            </div>
                        ))}

                        {/* Health & Dietary Section (Once for all) */}
                        <div className="bg-green-50 p-6 rounded-lg border border-green-200">
                            <h3 className="text-lg font-semibold text-green-900 mb-2">
                                🏥 Health & Dietary Requirements
                            </h3>
                            <p className="text-sm text-green-700 mb-4">
                                {numDivers > 1 
                                    ? 'Please list any health conditions or dietary needs for your entire group.'
                                    : 'Please let us know about any health conditions or dietary needs.'}
                            </p>
                            
                            <div className="space-y-6">
                                {/* Allergies Field */}
                                <div>
                                    <label htmlFor="allergies" className="block text-sm font-medium text-gray-700 mb-2">
                                        Allergies or Medical Conditions <span className="text-gray-500">(Optional)</span>
                                    </label>
                                    <textarea
                                        id="allergies"
                                        name="allergies"
                                        value={healthForm.allergies}
                                        onChange={handleHealthChange}
                                        rows={3}
                                        placeholder={numDivers > 1 
                                            ? "List any allergies or medical conditions for all divers (include diver names if applicable)..."
                                            : "e.g., Penicillin allergy, asthma, bee sting allergy..."}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
                                    />
                                    <p className="mt-1 text-sm text-gray-500">
                                        Important for dive safety briefings and emergency preparedness
                                    </p>
                                </div>

                                {/* Dietary Requirements Field */}
                                <div>
                                    <label htmlFor="dietary_requirements" className="block text-sm font-medium text-gray-700 mb-2">
                                        Dietary Requirements <span className="text-gray-500">(Optional)</span>
                                    </label>
                                    <textarea
                                        id="dietary_requirements"
                                        name="dietary_requirements"
                                        value={healthForm.dietary_requirements}
                                        onChange={handleHealthChange}
                                        rows={3}
                                        placeholder={numDivers > 1
                                            ? "List any dietary requirements for all divers..."
                                            : "e.g., Vegetarian, gluten-free, halal, nut allergy..."}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
                                    />
                                    <p className="mt-1 text-sm text-gray-500">
                                        For meal planning on camping adventures
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Submit Button */}
                        <button
                            type="submit"
                            disabled={submitting}
                            className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white py-4 px-6 rounded-lg font-semibold text-lg hover:from-orange-600 hover:to-orange-700 transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {submitting ? (
                                <span className="flex items-center justify-center">
                                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Saving...
                                </span>
                            ) : (
                                success ? '✅ Update Information' : '🚀 Complete My Profile'
                            )}
                        </button>
                    </form>

                    {/* Contact Info */}
                    <div className="mt-8 pt-6 border-t border-gray-200 text-center">
                        <p className="text-sm text-gray-500">
                            Questions? Contact us at{' '}
                            <a href="mailto:DiveCampAdventures@gmail.com" className="text-orange-600 hover:underline">
                                DiveCampAdventures@gmail.com
                            </a>
                            {' '}or WhatsApp{' '}
                            <a href="https://wa.me/61414710541" className="text-orange-600 hover:underline">
                                +61 414 710 541
                            </a>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CustomerDetailsPage;
